// Input-only fallback through the Gamepad API: works in any browser (and for
// wheels other than Logitech's), but browsers expose no wheel force feedback,
// so this path has no FFB.

import type { RawControls } from './hidReport';
import type { ControlSource } from './source';

/** Gamepad ids that look like a steering wheel. */
export function looksLikeWheel(id: string): boolean {
  return /wheel|g2[5579]|g9[23]|driving force|momo|fanatec|thrustmaster|t300|t150|t500|tmx|simucube|moza|g pro racing/i.test(id);
}

export class GamepadSource implements ControlSource {
  readonly kind = 'gamepad' as const;
  readonly controls: RawControls = { axes: [], buttons: [], hat: -1 };
  connected = true;
  private lastSteer: number | undefined;
  private lastAt = 0;
  private velocity = 0;
  velocityAxis = 0;

  constructor(
    readonly index: number,
    readonly id: string,
  ) {}

  get name(): string {
    return this.id.replace(/\s*\(.*?\)\s*/g, ' ').trim() || 'Game controller';
  }

  get key(): string {
    return `gp:${this.id}`;
  }

  get axisNames(): string[] {
    return this.controls.axes.map((_, i) => `Axis ${i}`);
  }

  get wheelVelocity(): number {
    return this.velocity;
  }

  poll(): void {
    const pad = navigator.getGamepads?.()[this.index];
    if (!pad || pad.id !== this.id) {
      this.connected = false;
      return;
    }
    this.connected = pad.connected;
    this.controls.axes.length = pad.axes.length;
    pad.axes.forEach((v, i) => {
      this.controls.axes[i] = (v + 1) / 2;
    });
    this.controls.buttons.length = pad.buttons.length;
    pad.buttons.forEach((b, i) => {
      this.controls.buttons[i] = b.pressed;
    });
    const now = performance.now();
    const w = this.controls.axes[this.velocityAxis];
    if (w !== undefined && this.lastSteer !== undefined && now > this.lastAt) {
      const dt = Math.min(0.05, (now - this.lastAt) / 1000);
      this.velocity += Math.min(1, dt / 0.02) * ((w - this.lastSteer) / dt - this.velocity);
    }
    this.lastSteer = w;
    this.lastAt = now;
  }
}
